import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt,
  faShieldAlt,
  faCheckCircle,
  faSpinner,
  faTimesCircle,
  faLock,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import './PhoneVerificationModal.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const OTP_RESEND_SECONDS = 60;

export default function PhoneVerificationModal() {
  const { user, token, updateUser, logout } = useAuth();

  // If user is not logged in, is admin, or already has a verified phone, don't show modal
  const needsVerification = Boolean(
    user && user.role !== 'admin' && (!user.phone || !user.phone.trim())
  );

  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resendsLeft, setResendsLeft] = useState(1);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

  const [verifyPhase, setVerifyPhase] = useState('idle'); // 'idle' | 'scanning' | 'success' | 'error'
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const startCountdown = () => {
    setCountdown(OTP_RESEND_SECONDS);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  if (!needsVerification) {
    return null;
  }

  // ── Step 1: Send OTP to Phone ─────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const cleanPhone = phone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 9) {
      setError('Please enter a valid phone number with country code (e.g. 255712345678).');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/profile/send-phone-otp`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ phone: cleanPhone }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.resends_left !== undefined) setResendsLeft(data.resends_left);
        throw new Error(data.message || 'Failed to send verification code.');
      }

      if (data.resends_left !== undefined) {
        setResendsLeft(data.resends_left);
      }

      setStep('otp');
      setInfo(`A 5-digit verification code was sent to ${cleanPhone}`);
      startCountdown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (countdown > 0 || resendsLeft <= 0) return;
    setError('');
    setInfo('');
    setLoading(true);

    const cleanPhone = phone.replace(/\D/g, '');
    try {
      const res = await fetch(`${API}/profile/send-phone-otp`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ phone: cleanPhone }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.resends_left !== undefined) setResendsLeft(data.resends_left);
        throw new Error(data.message || 'Failed to resend verification code.');
      }

      if (data.resends_left !== undefined) {
        setResendsLeft(data.resends_left);
      }

      setInfo('A new code has been sent to your phone.');
      startCountdown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP & Confirm ──────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 5) {
      setError('Please enter the 5-digit code sent to your phone.');
      return;
    }

    const cleanPhone = phone.replace(/\D/g, '');
    setVerifyPhase('scanning');
    setLoading(true);

    try {
      const res = await fetch(`${API}/profile/verify-phone`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ phone: cleanPhone, otp }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Invalid or expired verification code.');
      }

      setVerifyPhase('success');

      // Brief success animation before updating user context (which hides the modal)
      setTimeout(() => {
        if (data.user) {
          updateUser(data.user);
        } else {
          updateUser({ phone: cleanPhone });
        }
      }, 1600);
    } catch (err) {
      setVerifyPhase('error');
      setTimeout(() => {
        setVerifyPhase('idle');
        setLoading(false);
        setError(err.message);
      }, 1200);
    }
  };

  return (
    <div className="phone-verify-overlay">
      <div className="phone-verify-card">
        {/* Verification Animation Overlay during OTP check */}
        {verifyPhase !== 'idle' && (
          <div className={`pvm-anim-overlay pvm-anim-overlay--${verifyPhase}`}>
            <div className="pvm-anim-card">
              <div className="pvm-ring-wrap">
                <svg className="pvm-ring-svg" viewBox="0 0 100 100">
                  <circle className="pvm-ring-track" cx="50" cy="50" r="44" />
                  <circle
                    className={`pvm-ring-progress pvm-ring-progress--${verifyPhase}`}
                    cx="50"
                    cy="50"
                    r="44"
                  />
                </svg>
                <div className={`pvm-ring-icon pvm-ring-icon--${verifyPhase}`}>
                  {verifyPhase === 'success' ? (
                    <FontAwesomeIcon icon={faCheckCircle} />
                  ) : verifyPhase === 'error' ? (
                    <FontAwesomeIcon icon={faTimesCircle} />
                  ) : (
                    <FontAwesomeIcon icon={faShieldAlt} className="pvm-shield-pulse" />
                  )}
                </div>
              </div>

              {verifyPhase === 'scanning' && (
                <div className="pvm-scan-digits">
                  {otp.split('').map((d, i) => (
                    <span
                      key={i}
                      className="pvm-scan-digit"
                      style={{ animationDelay: `${i * 0.12}s` }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              )}

              <p className={`pvm-anim-label pvm-anim-label--${verifyPhase}`}>
                {verifyPhase === 'scanning' && 'Verifying code...'}
                {verifyPhase === 'success' && 'Phone Verified Successfully!'}
                {verifyPhase === 'error' && 'Verification Failed'}
              </p>

              {verifyPhase === 'scanning' && (
                <div className="pvm-dots">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Top Header */}
        <div className="pvm-header">
          <div className="pvm-badge-icon">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
          <h2>Verify Your Phone Number</h2>
          <p className="pvm-subtitle">
            Welcome back, <strong>{user.username}</strong>! Please verify your mobile phone number to secure your account and activate instant order delivery SMS notifications.
          </p>
        </div>

        {error && <div className="pvm-error-box">{error}</div>}
        {info && <div className="pvm-info-box">{info}</div>}

        {/* ── STEP 1: Phone input ── */}
        {step === 'phone' && (
          <form onSubmit={handleSendOtp} className="pvm-form">
            <div className="pvm-input-group">
              <label className="pvm-label">Mobile Phone Number</label>
              <div className="pvm-phone-input-wrap">
                <FontAwesomeIcon icon={faMobileAlt} className="pvm-input-icon" />
                <input
                  type="tel"
                  placeholder="e.g. 255712345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pvm-input"
                  autoFocus
                  required
                />
              </div>
              <span className="pvm-hint">
                Include your country code without the '+' sign (e.g. 255712345678 for Tanzania).
              </span>
            </div>

            <div className="pvm-benefits">
              <div className="pvm-benefit-item">
                <FontAwesomeIcon icon={faCheckCircle} className="pvm-check" />
                <span>Instant SMS alerts when your orders complete</span>
              </div>
              <div className="pvm-benefit-item">
                <FontAwesomeIcon icon={faCheckCircle} className="pvm-check" />
                <span>Deposit payment receipts &amp; wallet updates</span>
              </div>
              <div className="pvm-benefit-item">
                <FontAwesomeIcon icon={faCheckCircle} className="pvm-check" />
                <span>Account security &amp; exclusive discount offers</span>
              </div>
            </div>

            <button type="submit" className="pvm-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Sending Code...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} /> Send Verification Code
                </>
              )}
            </button>

            <div className="pvm-footer-action">
              <button type="button" className="pvm-logout-link" onClick={logout}>
                <FontAwesomeIcon icon={faLock} /> Sign in to another account
              </button>
            </div>
          </form>
        )}

        {/* ── STEP 2: OTP Verification ── */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="pvm-form">
            <div className="pvm-otp-step-wrap">
              <p className="pvm-otp-notice">
                We sent a 5-digit verification code to <strong>{phone}</strong>.
              </p>

              <div
                className={`pvm-otp-input-wrap ${
                  verifyPhase === 'error' ? 'pvm-input-shake' : ''
                }`}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="_ _ _ _ _"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="pvm-otp-input"
                  autoFocus
                  required
                />
              </div>

              <button type="submit" className="pvm-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Verifying...
                  </>
                ) : (
                  'Confirm & Activate Account'
                )}
              </button>

              <div className="pvm-resend-row">
                {countdown > 0 ? (
                  <span className="pvm-countdown">Resend code in {countdown}s</span>
                ) : resendsLeft > 0 ? (
                  <button
                    type="button"
                    className="pvm-resend-btn"
                    onClick={handleResend}
                    disabled={loading}
                  >
                    Resend code ({resendsLeft} {resendsLeft === 1 ? 'chance' : 'chances'} left)
                  </button>
                ) : (
                  <span className="pvm-limit-reached">
                    ⚠️ Resend limit reached. Please wait 3 hours to request a new code.
                  </span>
                )}
              </div>

              <button
                type="button"
                className="pvm-back-btn"
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                  setError('');
                  setInfo('');
                  setVerifyPhase('idle');
                }}
              >
                &larr; Change phone number
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

